import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchPhotoStats,
  fetchMetricsStats,
  fetchMetricsRate,
  fetchPhotoMeta,
  fetchCameraStats,
  fetchArchiveStatus,
  fetchDetectionStats,
  fetchEditorSendsStats,
  fetchAIQualityStats,
  fetchTrainingStats,
  fetchSystemStatus,
} from "../../../api";
import { REFRESH_INTERVALS } from "../constants";
import { calculateDerivedStats, calculateTotalPipelineMs } from "../utils";

/**
 * Hook for loading and managing all statistics data
 */
export default function useStatisticsData({ activeTab, fromDate, toDate, onTopBadge }) {
  // Core stats
  const [stats, setStats] = useState(null);
  const [metricsStats, setMetricsStats] = useState(null);
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Tab-specific data
  const [cameraStats, setCameraStats] = useState(null);
  const [archiveStatus, setArchiveStatus] = useState(null);
  const [detectionStats, setDetectionStats] = useState(null);
  const [editorSendsStats, setEditorSendsStats] = useState(null);
  const [aiQualityStats, setAIQualityStats] = useState(null);
  const [trainingStats, setTrainingStats] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);

  // Timeline-specific
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageMeta, setImageMeta] = useState(null);

  // Load initial data
  const loadData = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const [statsData, metricsData, rateData] = await Promise.all([
        fetchPhotoStats().catch(() => null),
        fetchMetricsStats(fromDate, toDate).catch(() => null),
        fetchMetricsRate().catch(() => null),
      ]);
      setStats(statsData);
      setMetricsStats(metricsData);
      setRate(rateData);
    } catch (e) {
      console.error("Failed to load statistics:", e);
    }
    setLoading(false);
    setMetricsLoading(false);
  }, [fromDate, toDate]);

  // Initial load and refresh interval
  useEffect(() => {
    // Schedule initial load on next tick to avoid sync setState warning
    const initialLoad = setTimeout(loadData, 0);
    const interval = setInterval(loadData, REFRESH_INTERVALS.stats);
    return () => {
      clearTimeout(initialLoad);
      clearInterval(interval);
    };
  }, [loadData]);

  // Load image meta when selected (async fetch only)
  useEffect(() => {
    if (!selectedImage?.image_id) return;
    let cancelled = false;
    fetchPhotoMeta(selectedImage.image_id)
      .then((data) => {
        if (!cancelled) setImageMeta(data);
      })
      .catch(() => {
        if (!cancelled) setImageMeta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedImage?.image_id]);

  // Reset imageMeta when no image selected (derived from selectedImage)
  const effectiveImageMeta = selectedImage?.image_id ? imageMeta : null;

  // Topbar rate badge — prefer the 5-minute measured window so brief
  // bursts stay visible. `per_min` (60s) drops to 0 within a minute of
  // a burst ending. See matrix/docs/STATISTICS_METRICS.md.
  useEffect(() => {
    const measured = rate?.per_min_5m ?? rate?.per_min ?? null;
    if (measured != null) {
      onTopBadge?.(`${measured.toFixed(2)} / min`);
    }
  }, [rate, onTopBadge]);


  // Load camera stats when camera tab is active
  useEffect(() => {
    if (activeTab !== "camera") return;
    if (cameraStats) return; // Already loaded
    fetchCameraStats(500)
      .then(setCameraStats)
      .catch((e) => console.error("Failed to load camera stats:", e));
  }, [activeTab, cameraStats]);

  // Load archive status when archive tab is active (with auto-refresh)
  useEffect(() => {
    if (activeTab !== "archive") return;
    const loadArchiveStatus = () => {
      fetchArchiveStatus()
        .then(setArchiveStatus)
        .catch((e) => console.error("Failed to load archive status:", e));
    };
    loadArchiveStatus();
    const interval = setInterval(loadArchiveStatus, REFRESH_INTERVALS.archive);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Load detection stats when detection tab is active
  useEffect(() => {
    if (activeTab !== "detection") return;
    if (detectionStats) return; // Already loaded
    fetchDetectionStats(500)
      .then(setDetectionStats)
      .catch((e) => console.error("Failed to load detection stats:", e));
  }, [activeTab, detectionStats]);

  // Load editor sends stats when tab is active
  useEffect(() => {
    if (activeTab !== "editor_sends") return;
    fetchEditorSendsStats(fromDate, toDate)
      .then(setEditorSendsStats)
      .catch((e) => console.error("Failed to load editor sends stats:", e));
  }, [activeTab, fromDate, toDate]);

  // Load AI quality stats when tab is active
  useEffect(() => {
    if (activeTab !== "model_quality") return;
    fetchAIQualityStats(500)
      .then(setAIQualityStats)
      .catch((e) => console.error("Failed to load AI quality stats:", e));
  }, [activeTab]);

  // Load training stats when tab is active
  useEffect(() => {
    if (activeTab !== "training") return;
    const loadTrainingStats = () => {
      fetchTrainingStats()
        .then(setTrainingStats)
        .catch((e) => console.error("Failed to load training stats:", e));
    };
    loadTrainingStats();
    const interval = setInterval(loadTrainingStats, REFRESH_INTERVALS.training);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Load system status when system tab is active
  useEffect(() => {
    if (activeTab !== "system") return;
    const loadSystemStatus = async () => {
      try {
        const data = await fetchSystemStatus();
        setSystemStatus(data);
      } catch (e) {
        console.error("Failed to load system status:", e);
      }
    };
    loadSystemStatus();
    const interval = setInterval(loadSystemStatus, REFRESH_INTERVALS.system);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Derived stats (memoized)
  const derivedStats = useMemo(() => calculateDerivedStats(stats), [stats]);

  // Total pipeline time (memoized)
  const totalPipelineMs = useMemo(
    () => calculateTotalPipelineMs(metricsStats),
    [metricsStats]
  );

  return {
    // Core stats
    stats,
    metricsStats,
    rate,
    loading,
    metricsLoading,
    derivedStats,
    totalPipelineMs,

    // Tab-specific data
    cameraStats,
    archiveStatus,
    detectionStats,
    editorSendsStats,
    aiQualityStats,
    trainingStats,
    systemStatus,

    // Timeline
    selectedImage,
    setSelectedImage,
    imageMeta: effectiveImageMeta,

    // Actions
    refresh: loadData,
  };
}
